import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { jobDocTitle } from "../../utils/format";
import JobOrderDocument from "./JobOrderDocument";

const DOC_WIDTH_PX = 794; // 210mm at 96 DPI
const DOC_HEIGHT_PX = 1123; // 297mm at 96 DPI

/* On narrow screens, shrink the A4 page to fit (reference autoScaleDocumentPreview). */
const fitDocument = (area, doc) => {
  if (!area || !doc) return;
  const available = area.clientWidth - 20;
  if (window.innerWidth <= 820 && available < DOC_WIDTH_PX && available > 200) {
    const ratio = available / DOC_WIDTH_PX;
    doc.style.transform = `scale(${ratio})`;
    doc.style.transformOrigin = "top center";
    doc.style.marginBottom = `-${DOC_HEIGHT_PX * (1 - ratio)}px`;
  } else {
    doc.style.transform = "none";
    doc.style.marginBottom = "0";
  }
};

export default function JobOrderPreview({ job, onClose }) {
  const { store, config } = useAuth();
  const showToast = useToast();
  const areaRef = useRef(null);
  const docRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  // The browser offers document.title as the file name for "Save as PDF" when printing.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = jobDocTitle(job);
    return () => {
      document.title = previousTitle;
    };
  }, [job]);

  useLayoutEffect(() => {
    let timer;
    const refit = () => fitDocument(areaRef.current, docRef.current);
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(refit, 150);
    };
    refit();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const downloadPdf = useCallback(async () => {
    const doc = docRef.current;
    if (!doc) return;
    showToast("Generating high quality PDF...");
    setDownloading(true);

    // Render the page at full size, not the on-screen mobile scale.
    doc.style.transform = "none";
    doc.style.marginBottom = "0";
    try {
      const { default: html2pdf } = await import("html2pdf.js");
      await html2pdf()
        .set({
          margin: 0,
          filename: `${jobDocTitle(job)}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false, scrollX: 0, scrollY: 0 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: [] },
        })
        .from(doc)
        .save();
      showToast("PDF downloaded successfully!");
    } catch (err) {
      showToast("Direct printing document instead...", "info");
      window.print();
    } finally {
      fitDocument(areaRef.current, doc);
      setDownloading(false);
    }
  }, [job, showToast]);

  return (
    <div id="preview-modal" className="modal-backdrop no-print">
      <div className="modal-container preview-modal-container">
        <div className="preview-toolbar">
          <div className="preview-title">
            <span className="preview-badge">Document Preview</span>
            <span>{job.job_no}</span>
          </div>
          <div className="preview-actions">
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>
              <svg viewBox="0 0 24 24" className="btn-icon"><path fill="currentColor" d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" /></svg>
              Print Job Order
            </button>
            <button type="button" className="btn btn-success" onClick={downloadPdf} disabled={downloading}>
              <svg viewBox="0 0 24 24" className="btn-icon"><path fill="currentColor" d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
              {downloading ? "Generating..." : "Download PDF"}
            </button>
            <button type="button" className="btn-icon-close" onClick={onClose} aria-label="Close preview">
              &times;
            </button>
          </div>
        </div>

        <div className="preview-scroll-area" ref={areaRef}>
          <JobOrderDocument ref={docRef} job={job} store={store} config={config} />
        </div>
      </div>
    </div>
  );
}
